import { TaskQueue } from "@citychallenge/common";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { DownloadStore } from "./download-store";
import { DownloadTask } from "./download-task";
import { reaction } from "mobx";

export interface DownloadManager {
  enqueue: (task: DownloadTask[]) => void;
}

export const DownloadManagerContext = React.createContext<DownloadManager>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enqueue: (task: DownloadTask[]): void => {
    // Noop
  },
});

export interface DownloadManagerProps {
  /**
   * Files that this download madnager needs to download.
   * Note: only files that are supplied on first mount are loaded
   */
  store: DownloadStore;
  concurrency?: number;
  onComplete?: (store: DownloadStore) => void;
  children?: React.ReactNode;
}

export const DownloadManager = forwardRef<
  DownloadManager,
  DownloadManagerProps
>(({ store, concurrency = 3, onComplete, children }, ref) => {
  const queue = useRef<TaskQueue>();

  useEffect(() => {
    const taskQueue = new TaskQueue({ concurrency });
    queue.current = taskQueue;

    const disposer = reaction(
      () => store.completed,
      () => {
        onComplete?.(store);
      }
    );

    taskQueue.enqueue(store.allDownloadTasks);

    return () => {
      for (const { destroy } of store.all) {
        destroy?.();
      }
      taskQueue.stop();
      disposer();
    };
  }, [store]);

  function enqueueDownloadTasks(tasks: DownloadTask[]) {
    queue.current?.enqueue(tasks.map(({ download }) => download));
  }

  useImperativeHandle(ref, () => ({
    enqueue: enqueueDownloadTasks,
  }));

  return (
    <DownloadManagerContext.Provider value={{ enqueue: enqueueDownloadTasks }}>
      <>{children}</>
    </DownloadManagerContext.Provider>
  );
});
